import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { isLocalMode, getLocalAppContext } from '@/services/localRuntimeClient';
import { useMemo } from 'react';
import { apiVersions, getLogicEndpoint } from '@/services/endpoints';
import { getAppFromId } from './useApps';
import { parseAppId } from '@/types';
import { getSiteMasterKeyProduction } from '@/services/azureMasterKeyService';

interface RuntimeDetails {
  hostName: string | undefined;
  masterKey: string | undefined;
}

export const useRuntimeDetails = (projectName?: string, appName?: string) => {
  const { selectedApp } = useAppStore();
  const project = useMemo(() => projectName ?? selectedApp?.projectName, [projectName, selectedApp]);
  const app = useMemo(() => appName ?? selectedApp?.name, [appName, selectedApp]);
  
  return useQuery({
    queryKey: ['appRuntimeDetails', project?.toLowerCase(), app?.toLowerCase()],
    queryFn: async (): Promise<RuntimeDetails> => {
      if (isLocalMode()) {
        const localCtx = getLocalAppContext();
        return {
          hostName: localCtx.defaultHostName,
          masterKey: 'dummykey', // In local mode, master key is not needed as we use a proxy
        };
      }

      // Any code path calling this api will not work in HOBO apps, so we are just returning dummy data here to avoid error.
      // Once we support the apis using this, this method will be removed.
      return {
        hostName: `${app}-dummyhostname`, // Placeholder hostname for non-local mode
        masterKey: `${app}dummykey`, // Placeholder master key for non-local mode
      };
    },
    enabled: !!project && !!app,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

export const useRuntimeDetailsOld = (projectName?: string, appName?: string) => {
  const { getOttoToken, getArmToken, isAuthenticated } = useAuth();
  const { selectedApp } = useAppStore();
  const queryClient = useQueryClient();
  const project = useMemo(() => projectName ?? selectedApp?.projectName, [projectName, selectedApp]);
  const app = useMemo(() => appName ?? selectedApp?.name, [appName, selectedApp]);
  const armResourceId = useMemo(() => selectedApp?.properties?.appResourceId, [selectedApp]);
  
  return useQuery({
    queryKey: ['appRuntimeDetails', project?.toLowerCase(), app?.toLowerCase()],
    queryFn: async (): Promise<RuntimeDetails> => {
      if (isLocalMode()) {
        const localCtx = getLocalAppContext();
        return {
          hostName: localCtx.defaultHostName,
          masterKey: 'dummykey', // In local mode, master key is not needed as we use a proxy
        };
      }

      // Try listKeys API first - it provides both fqdn and masterKey
      let hostName: string | undefined;
      let masterKey = '';
      let listKeysFailed = false;

      try {
        const token = await getOttoToken();
        if (token) {
          const response = await fetch(`${getLogicEndpoint()}/automationProjects/${project}/applications/${app}/listKeys?api-version=${apiVersions.otto}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const data = await response.json();
            hostName = data.fqdn;
            masterKey = data.masterKey ?? '';
          } else {
            listKeysFailed = true;
          }
        } else {
          listKeysFailed = true;
        }
      } catch (error) {
        console.warn('Failed to fetch master key from list keys call:', error);
        listKeysFailed = true;
      }

      if (listKeysFailed && armResourceId) {
        const result = await fetchDetailsFromACA(queryClient, armResourceId ?? '', getArmToken);
        if (result && result.appHostName) {
          return { hostName: result.appHostName, masterKey: result.masterKey };
        } else {
          const app = await getAppFromId(queryClient, armResourceId, getArmToken);
          return { hostName: app?.properties?.configuration?.ingress?.fqdn ?? '', masterKey: '' };
        }
      }

      return {
        hostName,
        masterKey,
      };
    },
    enabled: (isAuthenticated || isLocalMode()) && !!project && !!app,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

const fetchDetailsFromACA = (queryClient: QueryClient, apiResourceId: string, getToken: () => Promise<string | null>) => {
  return queryClient.fetchQuery({
    queryKey: ['appRuntimeDetails', apiResourceId],
    queryFn: async (): Promise <{ appHostName: string; masterKey: string; } | null> => {
      const parsed = parseAppId(apiResourceId);
      if (!parsed) {
        throw new Error('Invalid app resource ID');
      }
      const accessToken = await getToken();
      if (!accessToken) {
        throw new Error('Unable to acquire access token');
      }

      return getSiteMasterKeyProduction({
        subscriptionId: parsed.subscriptionId,
        resourceGroup: parsed.resourceGroup,
        appName: parsed.appName,
        armAccessToken: accessToken,
      });
    },
  });
};


